// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { CheckIcon, XmarkIcon, FileIcon, CircleInfoIcon, PenIcon } from '@revfluence/fresh-icons/regular/esm';
import { useCustomStages } from '../context/CustomStagesContext';

// Keep these for consistent UI until we get real data
const BADGE_COLORS = {
  '2025 Product Launch': 'bg-indigo-200',
  'Spring Sale Campaign': 'bg-blue-300',
  'Mother\'s Day Campaign': 'bg-emerald-200',
  'New Product Launch': 'bg-emerald-200'
};

interface Props {
  editedName?: string;
  setEditedName?: (name: string) => void;
  isNew?: boolean;
}

export const StageNameSection: React.FC<Props> = ({
  editedName = 'New Custom Stage',
  setEditedName = () => {},
  isNew = false
}) => {
  const { selectedStage, updateStage } = useCustomStages();
  const [isEditing, setIsEditing] = useState(false);
  const [localName, setLocalName] = useState(editedName);

  // Update local name when editedName changes
  useEffect(() => {
    if (editedName) {
      setLocalName(editedName);
    }
  }, [editedName]);

  const handleSave = () => {
    const trimmedName = localName?.trim() || '';
    if (!trimmedName) return;

    // Always update parent state when saving
    setEditedName(trimmedName);

    if (!isNew && selectedStage) {
      updateStage(selectedStage.id, { name: trimmedName });
    }
    setIsEditing(false);
  };

  return (
    <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-indigo-500 rounded-lg border border-gray-300 flex items-center justify-center">
          <FileIcon className="w-3 h-3 text-white" />
        </div>

        <div className="flex-1 flex items-center gap-2">
          {isEditing ? (
            <>
              <Input
                value={localName || ''}
                onChange={(e) => setLocalName(e.target.value)}
                className="flex-1 h-8 px-3 py-1 text-sm"
                autoFocus
                placeholder="Enter stage name"
              />
              <button
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                onClick={() => {
                  setLocalName(editedName || '');
                  setIsEditing(false);
                }}
              >
                <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <button
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                onClick={handleSave}
                disabled={!localName?.trim()}
              >
                <CheckIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
            </>
          ) : (
            <>
              <span className="text-sm font-medium text-gray-800">
                {localName || editedName || 'New Custom Stage'}
              </span>
              <button
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"
                onClick={() => setIsEditing(true)}
              >
                <PenIcon className="w-3.5 h-3.5 text-gray-800" />
              </button>
              <div className="flex-1" />
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-800">Current Campaigns</span>
        <CircleInfoIcon className="w-3 h-3 text-gray-300" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(selectedStage?.campaigns || []).map((campaign) => (
          <Badge
            key={campaign.id}
            variant="secondary"
            className={`px-2 py-0.5 text-xs font-normal text-gray-800 ${BADGE_COLORS[campaign.name] || 'bg-gray-200'}`}
          >
            {campaign.name}
          </Badge>
        ))}
        {selectedStage?.remainingCampaigns ? (
          <Badge
            variant="secondary"
            className="px-2 py-0.5 text-xs font-normal text-gray-800 bg-gray-200"
          >
            +{selectedStage.remainingCampaigns}
          </Badge>
        ) : null}
      </div>
    </div>
  );
};
